import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  type AccessLogRow,
  type ClientUsageResponse,
  getAccessLogs,
  getClientUsage,
} from '../../api/client-usage'
import { listOAuthGrants, revokeOAuthGrant } from '../../api/mcp-oauth'
import type { McpOAuthGrant } from '../../types/api'
import { useDialog } from '../dialogs/useDialog'

interface UserClientsDrawerProps {
  userId: number
  mcpKeyPrefix?: string | null
  mcpKeyLastUsedAt?: string | null
  onRotateKey: () => void
  isRotatingKey: boolean
}

export function UserClientsDrawer({
  userId,
  mcpKeyPrefix,
  mcpKeyLastUsedAt,
  onRotateKey,
  isRotatingKey,
}: UserClientsDrawerProps) {
  const dialog = useDialog()
  const [grants, setGrants] = useState<McpOAuthGrant[] | null>(null)
  const [isLoadingGrants, setIsLoadingGrants] = useState(true)
  const [usage, setUsage] = useState<ClientUsageResponse | null>(null)
  const [isLoadingUsage, setIsLoadingUsage] = useState(true)
  const [accessLogs, setAccessLogs] = useState<AccessLogRow[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState(true)
  const [revokingId, setRevokingId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    listOAuthGrants()
      .then((data) => {
        if (!cancelled) setGrants(data.grants.filter((g) => String(g.user_email) !== '')) // grants are org-wide; filter by user is done below
      })
      .catch(() => {
        if (!cancelled) setGrants(null) // 501 = OAuth disabled
      })
      .finally(() => {
        if (!cancelled) setIsLoadingGrants(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Client-usage endpoint is org-wide; we filter per-user in the UI.
  useEffect(() => {
    let cancelled = false
    getClientUsage(userId)
      .then((data) => {
        if (!cancelled) setUsage(data)
      })
      .catch(() => {
        if (!cancelled) setUsage(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingUsage(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  useEffect(() => {
    let cancelled = false
    getAccessLogs(userId, 20)
      .then((data) => {
        if (!cancelled) setAccessLogs(data)
      })
      .catch(() => {
        if (!cancelled) setAccessLogs([])
      })
      .finally(() => {
        if (!cancelled) setIsLoadingLogs(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  const handleRevoke = useCallback(
    async (id: number) => {
      const confirmed = await dialog.confirm({
        title: 'Revoke OAuth grant?',
        description: 'The client will lose MCP access immediately.',
        confirmLabel: 'Revoke grant',
        tone: 'danger',
      })
      if (!confirmed) return

      setRevokingId(id)
      try {
        await revokeOAuthGrant(id)
        setGrants((prev) =>
          prev
            ? prev.map((g) => (g.id === id ? { ...g, revoked_at: new Date().toISOString() } : g))
            : prev
        )
        toast.success('OAuth grant revoked')
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to revoke')
      } finally {
        setRevokingId(null)
      }
    },
    [dialog]
  )

  // Per-user usage from the org-wide response.
  const userUsage = usage?.perUser.find((u) => u.userId === userId)
  const userToolCalls = usage?.perTool ?? []

  return (
    <div className="border-t border-harbor-border bg-harbor-surface/30 px-6 py-4 space-y-6">
      {/* OAuth Grants */}
      <section>
        <h4 className="text-sm font-semibold text-harbor-text mb-2">OAuth Grants</h4>
        {isLoadingGrants ? (
          <p className="text-xs text-harbor-text-muted">Loading…</p>
        ) : grants === null ? (
          <p className="text-xs text-harbor-text-muted">OAuth not configured.</p>
        ) : grants.length === 0 ? (
          <p className="text-xs text-harbor-text-muted">No OAuth grants.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="text-harbor-text-muted">
                <tr>
                  <th className="text-left pr-4 py-1">Client ID</th>
                  <th className="text-left pr-4 py-1">Scope</th>
                  <th className="text-left pr-4 py-1">Granted</th>
                  <th className="text-left pr-4 py-1">Status</th>
                  <th className="text-right py-1">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-harbor-border">
                {grants.map((g) => (
                  <tr key={g.id}>
                    <td className="pr-4 py-1.5 font-mono text-harbor-text truncate max-w-[12rem]">
                      {g.client_id}
                    </td>
                    <td className="pr-4 py-1.5 text-harbor-text-secondary">{g.scope}</td>
                    <td className="pr-4 py-1.5 text-harbor-text-secondary">
                      {new Date(g.granted_at).toLocaleDateString()}
                    </td>
                    <td className="pr-4 py-1.5">
                      {g.revoked_at ? (
                        <span className="text-red-400">Revoked</span>
                      ) : (
                        <span className="text-emerald-400">Active</span>
                      )}
                    </td>
                    <td className="py-1.5 text-right">
                      {!g.revoked_at && (
                        <button
                          type="button"
                          disabled={revokingId === g.id}
                          onClick={() => void handleRevoke(g.id)}
                          className="text-red-400 hover:text-red-300 disabled:opacity-50"
                        >
                          {revokingId === g.id ? 'Revoking…' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* API Key */}
      <section>
        <h4 className="text-sm font-semibold text-harbor-text mb-2">MCP API Key</h4>
        {mcpKeyPrefix ? (
          <div className="flex items-center gap-4 text-sm">
            <span className="font-mono text-harbor-text">{mcpKeyPrefix}…</span>
            <span className="text-harbor-text-muted">
              {mcpKeyLastUsedAt
                ? `Last used ${new Date(mcpKeyLastUsedAt).toLocaleString()}`
                : 'Never used'}
            </span>
            <button
              type="button"
              disabled={isRotatingKey}
              onClick={onRotateKey}
              className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 text-xs"
            >
              {isRotatingKey ? 'Rotating…' : 'Rotate'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-harbor-text-muted">No MCP key.</p>
        )}
      </section>

      {/* Usage */}
      <section>
        <h4 className="text-sm font-semibold text-harbor-text mb-2">Usage (last 7 days)</h4>
        {isLoadingUsage ? (
          <p className="text-xs text-harbor-text-muted">Loading…</p>
        ) : !userUsage || userUsage.clients.length === 0 ? (
          <p className="text-xs text-harbor-text-muted">No usage data.</p>
        ) : (
          <div className="space-y-3">
            <div>
              <p className="text-xs text-harbor-text-muted mb-1">Top Clients</p>
              <div className="flex flex-wrap gap-2">
                {userUsage.clients.map((c) => (
                  <span
                    key={c.client}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-harbor-surface-raised text-harbor-text-secondary text-xs"
                  >
                    {c.client || 'unknown'}
                    <span className="text-harbor-text-muted">({c.calls})</span>
                  </span>
                ))}
              </div>
            </div>
            {userToolCalls.length > 0 && (
              <div>
                <p className="text-xs text-harbor-text-muted mb-1">Per-Tool Calls</p>
                <div className="flex flex-wrap gap-2">
                  {userToolCalls.slice(0, 10).map((t) => (
                    <span
                      key={t.tool}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-harbor-surface-raised text-harbor-text-secondary text-xs"
                    >
                      {t.tool}
                      <span className="text-harbor-text-muted">({t.calls})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Recent Access Logs */}
      <section>
        <h4 className="text-sm font-semibold text-harbor-text mb-2">Recent Connections</h4>
        {isLoadingLogs ? (
          <p className="text-xs text-harbor-text-muted">Loading…</p>
        ) : accessLogs.length === 0 ? (
          <p className="text-xs text-harbor-text-muted">No recent connections.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="text-harbor-text-muted">
                <tr>
                  <th className="text-left pr-4 py-1">Client</th>
                  <th className="text-left pr-4 py-1">IP</th>
                  <th className="text-left pr-4 py-1">Location</th>
                  <th className="text-left pr-4 py-1">Path</th>
                  <th className="text-left py-1">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-harbor-border">
                {accessLogs.map((log, i) => (
                  <tr key={`${log.ip}-${log.timestamp}-${i}`}>
                    <td className="pr-4 py-1 text-harbor-text">{log.client || '—'}</td>
                    <td className="pr-4 py-1 font-mono text-harbor-text-secondary">{log.ip}</td>
                    <td className="pr-4 py-1 text-harbor-text-secondary">
                      {[log.city, log.region, log.country].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="pr-4 py-1 font-mono text-harbor-text-secondary">{log.path}</td>
                    <td className="py-1 text-harbor-text-muted">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
